import { useMutation } from '@apollo/client/react';
import { useWaitForWorkspace } from '@components/Onboarding/use-wait-for-workspace';
import { ReportCelloSignupDocument } from '@graphql/mutations/cello/report-signup.graphql';
import { CELLO_REFERRAL_PAYWALL_ROUTE } from '@modules/cello-referral';
import { createChildLogger } from '@modules/logger';
import Router from 'next/router';
import { useCallback } from 'react';

import { useCelloReferralEligibility } from './use-cello-referral-eligibility';

const logger = createChildLogger({ module: 'hooks/cello/use-cello-referral-completion' });

export interface CelloReferralCompletion {
  /**
   * Attributes a newly created referred account to Cello. No-op for an account that
   * wasn't referred or isn't eligible, so it is safe to call from any signup exit.
   *
   * Only call this where the authentication actually created the account: reporting a
   * returning user would attribute a referral to an account that was never referred.
   * The API re-checks that against the account record; the callers' gate only saves a
   * round trip on the case the client already knows about.
   */
  reportReferredSignup: (userId: string) => Promise<void>;

  /**
   * Hands a referred user to the discounted paywall.
   *
   * Returns whether it took over navigation, so a caller can fall through to its own
   * destination when the user isn't referred or isn't eligible.
   */
  routeToReferralPaywall: (
    userId: string,
    options?: { isReturningUser?: boolean },
  ) => Promise<boolean>;
}

/**
 * The completion path for a referred account, shared by every onboarding exit that can
 * finish a signup — direct signup, the login page's new-account detour, and the
 * customize-card exit an already-authenticated user takes.
 *
 * Centralized because the two halves are easy to lose independently: an exit that skips
 * the report drops the referrer's attribution, and one that skips the route drops the
 * user's discount (ACT-2224). Both are gated on the same cached code and flag, and both
 * need the workspace to exist first.
 */
export function useCelloReferralCompletion(): CelloReferralCompletion {
  const [reportCelloSignup] = useMutation(ReportCelloSignupDocument);
  const { waitForWorkspace } = useWaitForWorkspace();
  const { resolveReferralCode } = useCelloReferralEligibility();

  /**
   * The personal workspace and owner profile are provisioned after the account itself,
   * and both halves of completion need them: the API resolves the caller from the
   * Postgres user row that same provisioning creates, and the paywall's checkout reads
   * the owner profile. Waiting here puts the delay behind the signup spinner instead of
   * under a "Claim" tap that would otherwise race provisioning and fail.
   *
   * A failed wait is logged and continued past rather than thrown: it is more likely a
   * slow provision than a missing one, and abandoning would cost the user their discount.
   */
  const waitForProvisioning = useCallback(
    async (userId: string) => {
      try {
        await waitForWorkspace(userId);
      } catch (error) {
        logger.error('Workspace was not ready for the Cello referral completion', {
          error,
          user_id: userId,
        });
      }
    },
    [waitForWorkspace],
  );

  const reportReferredSignup = useCallback(
    async (userId: string) => {
      const code = await resolveReferralCode();

      if (!code) {
        return;
      }

      await waitForProvisioning(userId);

      // Dispatched rather than awaited: no caller reads the result, and a slow Cello
      // would otherwise hold the signup spinner open. Every caller navigates with a
      // client-side transition, so the request outlives the page change.
      reportCelloSignup({ variables: { code } }).catch((error) => {
        logger.error('Failed to report the Cello signup', { error, user_id: userId });
      });
    },
    [reportCelloSignup, resolveReferralCode, waitForProvisioning],
  );

  const routeToReferralPaywall = useCallback(
    async (userId: string, { isReturningUser = false }: { isReturningUser?: boolean } = {}) => {
      // Routed on the cached code alone rather than a validated one: validation is
      // login-gated and would add a network round trip here. The paywall validates, and
      // forwards to this same next step when the code turns out to be unusable.
      const code = await resolveReferralCode();

      if (!code) {
        return false;
      }

      await waitForProvisioning(userId);

      await Router.replace(
        isReturningUser
          ? `${CELLO_REFERRAL_PAYWALL_ROUTE}?returning=1`
          : CELLO_REFERRAL_PAYWALL_ROUTE,
      );

      return true;
    },
    [resolveReferralCode, waitForProvisioning],
  );

  return { reportReferredSignup, routeToReferralPaywall };
}
